import { biText } from '@euler/app/components/message/widgets/bi-visualization/functions/biText';
import { kBiGrainLabels } from '@euler/app/components/message/widgets/bi-visualization/functions/biVizLabels';
import { Typography } from '@euler/app/components/typography';
import { CardNotice } from '@euler/app/flows/ai-suite/components/ChatCard';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiConformedKey } from '@euler/model/bi/catalog';
import { memo } from 'react';
import { View } from 'react-native';
import { BiChip } from '../../../components/ui/BiChip';
import { EditorSection } from '../EditorSection';
import {
  BiBlendCatalogs,
  biBlendGrains,
  biBlendKeyOptions,
  biBlendSideDimension,
  biEditorWithBlendField,
  biEditorWithBlendGrain,
  biEditorWithBlendKey,
  biEditorWithoutBlendKey,
  kBiMaxBlendKeys,
} from '../functions/biEditorBlend';
import { kBiConformedKeyLabels } from '../functions/biEditorLabels';
import { BiEditorBlend, BiEditorState } from '../functions/biEditorState';

/**
 * What the two datasets line up on: the conformed keys both carry, a
 * date's grain both fields allow, and which field stands for a key on a
 * side that has several.
 */
export const BlendKeysSection = memo(
  ({
    state,
    blend,
    catalogs,
    onChange,
  }: {
    state: BiEditorState;
    blend: BiEditorBlend;
    catalogs: BiBlendCatalogs;
    onChange: (next: BiEditorState) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const options = biBlendKeyOptions(catalogs);
    const row = {
      flexDirection: 'row' as const,
      flexWrap: 'wrap' as const,
      alignItems: 'center' as const,
      gap: rpx(6),
    };
    const sides = [state, blend.second];
    // Keys the card has that the catalogs no longer offer can be removed.
    const stray = blend.on.filter(
      on => !options.some(option => option.key === on.key),
    );
    return (
      <EditorSection title="关联字段" note="两份数据按它对齐">
        {!options.length ? (
          <CardNotice text="这两份数据没有共同的关联字段（如日期、车牌、技师），无法组合。" />
        ) : null}
        <View style={row}>
          {stray.map(on => (
            <BiChip
              key={on.key}
              label={`${kBiConformedKeyLabels[on.key]} ×`}
              selected
              onPress={() =>
                onChange(
                  biEditorWithoutBlendKey(state, blend, catalogs, on.key),
                )
              }
            />
          ))}
          {options.map(option => {
            const selected = blend.on.some(entry => entry.key === option.key);
            const added = selected
              ? null
              : biEditorWithBlendKey(state, blend, catalogs, option);
            return (
              <BiChip
                key={option.key}
                label={kBiConformedKeyLabels[option.key]}
                selected={selected}
                disabled={!selected && !added}
                onPress={() =>
                  onChange(
                    selected
                      ? biEditorWithoutBlendKey(
                          state,
                          blend,
                          catalogs,
                          option.key,
                        )
                      : added ?? state,
                  )
                }
              />
            );
          })}
        </View>
        {blend.on.length >= kBiMaxBlendKeys ? (
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            最多 {kBiMaxBlendKeys} 个关联字段
          </Typography.Text>
        ) : null}
        {blend.on.map(on => {
          const option = options.find(entry => entry.key === on.key);
          if (!option) return null;
          const picked = sides.map((query, side) =>
            option.fields[side].find(
              field =>
                field.key ===
                biBlendSideDimension(catalogs[side], query, on.key)?.field,
            ),
          );
          return (
            <View key={on.key} style={{ gap: rpx(6) }}>
              {option.fields.map((fields, side) =>
                fields.length > 1 ? (
                  <View key={side} style={row}>
                    <Typography.Text
                      size={rpx(12)}
                      color={aiColors.mutedForeground}
                    >
                      {biText(catalogs[side].label)}的
                      {kBiConformedKeyLabels[on.key]}
                    </Typography.Text>
                    {fields.map(field => {
                      const next = biEditorWithBlendField(
                        state,
                        blend,
                        catalogs,
                        side,
                        on.key,
                        field,
                      );
                      return (
                        <BiChip
                          key={field.key}
                          label={biText(field.label)}
                          selected={picked[side]?.key === field.key}
                          disabled={!next}
                          onPress={() => (next ? onChange(next) : undefined)}
                        />
                      );
                    })}
                  </View>
                ) : null,
              )}
              {on.key === BiConformedKey.Date ? (
                <View style={row}>
                  <Typography.Text
                    size={rpx(12)}
                    color={aiColors.mutedForeground}
                  >
                    日期粒度
                  </Typography.Text>
                  {biBlendGrains([picked[0], picked[1]]).map(grain => (
                    <BiChip
                      key={grain}
                      label={kBiGrainLabels[grain]}
                      selected={on.grain === grain}
                      onPress={() =>
                        onChange(
                          biEditorWithBlendGrain(state, blend, catalogs, grain),
                        )
                      }
                    />
                  ))}
                </View>
              ) : null}
            </View>
          );
        })}
      </EditorSection>
    );
  },
);
